import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  Target, Scale, Ruler, Camera, Cake, Bell, BellOff, Settings, Check, Loader2, MapPin, Crown, X,
  Mail, Calendar, Quote, ChevronRight, Trophy
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { UserBadge } from '../UserBadge';
import { BADGE_CATALOG } from '../../utils/badges';
import { useNavigate } from 'react-router-dom';
import { unitLabel } from '../strength/units';
import { useWeightUnit } from '../../hooks/useWeightUnit';

const API = process.env.REACT_APP_BACKEND_URL;

export const ProfileCard = ({ 
  user, 
  profilePicture, 
  uploadingProfile, 
  onProfilePictureUpload,
  formData,
  goalDate,
  daysUntilGoal,
  notificationsEnabled,
  loadingNotifications,
  onEnableNotifications,
  onDisableNotifications,
  showNotificationSettings,
  onToggleNotificationSettings,
  notificationSettings,
  onNotificationSettingsChange,
  onSaveNotificationSettings,
  onToggleDay,
  isPremium,
  subscriptionPlan
}) => {
  const { unit: weightUnit } = useWeightUnit();
  const profileInputRef = useRef(null);
  const [enlargedImage, setEnlargedImage] = useState(null);

  const imageUrl = profilePicture ? (profilePicture.startsWith('http') ? profilePicture : `${API}${profilePicture}`) : null;
  const navigate = useNavigate();
  const earnedBadges = (user?.badges || []).filter((c) => BADGE_CATALOG[c]);
  const totalBadges = Object.keys(BADGE_CATALOG).length;

  return (
    <>
    <motion.div 
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.1 }}
      className="relative rounded-2xl border border-white/10 bg-gradient-to-b from-zinc-900/80 to-black/60 shadow-[0_8px_30px_rgba(0,0,0,0.35)] overflow-hidden"
      data-testid="profile-card"
    >
      {/* Ambient glow orbs */}
      <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-48 w-48 rounded-full bg-primary/5 blur-3xl" />

      <div className="relative p-6 sm:p-8">
      <div className="flex flex-col items-center text-center mb-6">
        {/* Profile Picture Upload */}
        <div className="relative inline-block mb-5">
          {/* Outer glow ring for premium */}
          {isPremium && (
            <span className="pointer-events-none absolute inset-0 -m-1 rounded-full bg-gradient-to-tr from-yellow-500/40 via-yellow-300/20 to-yellow-500/40 blur-md" />
          )}
          <div
            className={`relative h-28 w-28 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center cursor-pointer transition-all ${
              isPremium
                ? 'ring-2 ring-yellow-400/70 ring-offset-4 ring-offset-black hover:ring-yellow-300'
                : 'ring-2 ring-primary/40 ring-offset-4 ring-offset-black hover:ring-primary'
            }`}
            onClick={() => profileInputRef.current?.click()}
            data-testid="profile-picture-container"
          >
            {uploadingProfile ? (
              <Loader2 className="h-8 w-8 text-primary animate-spin" />
            ) : profilePicture ? (
              <img
                src={imageUrl}
                alt="Profile"
                className="w-full h-full object-cover"
                data-testid="profile-picture-image"
                onClick={(e) => { e.stopPropagation(); setEnlargedImage(imageUrl); }}
              />
            ) : (
              <span className="font-heading text-4xl text-primary">
                {user?.name?.charAt(0)?.toUpperCase() || 'A'}
              </span>
            )}
          </div>
          <button
            onClick={() => profileInputRef.current?.click()}
            className="absolute -bottom-1 -right-1 h-9 w-9 bg-primary rounded-full flex items-center justify-center hover:bg-primary/90 transition-all shadow-[0_4px_14px_rgba(250,204,21,0.35)] ring-2 ring-black"
            disabled={uploadingProfile}
            data-testid="upload-profile-picture-btn"
            aria-label="Upload profile picture"
          >
            <Camera className="h-4 w-4 text-black" />
          </button>
          <input
            ref={profileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={onProfilePictureUpload}
            className="hidden"
            data-testid="profile-picture-input"
          />
          {/* Milestone badges overlay */}
          {(user?.badges || []).length > 0 && (
            <div className="absolute -top-1 -left-1 flex flex-col gap-1">
              {(user.badges || []).slice(0, 3).map((code) => (
                <UserBadge key={code} code={code} size="md" />
              ))}
            </div>
          )}
        </div>

        {/* Premium Badge — sits cleanly below avatar */}
        {isPremium && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center gap-1.5 mb-4 px-3 py-1 bg-gradient-to-r from-yellow-500/15 to-yellow-500/5 border border-yellow-500/40 rounded-full shadow-[0_4px_14px_rgba(234,179,8,0.15)]"
            data-testid="premium-badge"
          >
            <Crown className="h-3.5 w-3.5 text-yellow-400" />
            <span className="text-yellow-300 font-heading text-[11px] uppercase tracking-[0.2em]">
              Premium {subscriptionPlan === 'yearly' ? 'Yearly' : 'Member'}
            </span>
          </motion.div>
        )}

        {/* Name — gradient */}
        <h3
          className="font-heading text-2xl sm:text-3xl uppercase tracking-wider bg-gradient-to-b from-white to-white/70 bg-clip-text text-transparent leading-tight"
          data-testid="profile-name"
        >
          {user?.name}
        </h3>

        {/* Meta chips */}
        <div className="flex flex-wrap items-center justify-center gap-1.5 mt-3">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-white/70 bg-white/[0.04] border border-white/10 rounded-full px-2.5 py-1 max-w-full">
            <Mail className="h-3 w-3 text-primary flex-shrink-0" />
            <span className="truncate">{user?.email}</span>
          </span>
          {formData.location && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-primary bg-primary/[0.08] border border-primary/25 rounded-full px-2.5 py-1">
              <MapPin className="h-3 w-3" />
              {formData.location}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-white/60 bg-white/[0.03] border border-white/10 rounded-full px-2.5 py-1">
            <Calendar className="h-3 w-3" />
            Since {new Date(user?.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
          </span>
        </div>

        {/* Bio blockquote */}
        {formData.bio && (
          <div className="relative w-full mt-5 pl-4 text-left">
            <span className="absolute left-0 top-0 bottom-0 w-[2px] rounded-full bg-gradient-to-b from-primary/60 via-primary/25 to-transparent" />
            <Quote className="absolute -left-1 -top-1 h-4 w-4 text-primary/40" />
            <p className="text-white/80 text-sm italic leading-relaxed pl-3">
              {formData.bio}
            </p>
          </div>
        )}
      </div>

      {/* Goal Progress */}
      {goalDate && daysUntilGoal > 0 && (
        <div className="relative rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/[0.1] via-primary/[0.04] to-transparent p-5 mt-6 overflow-hidden">
          <div className="pointer-events-none absolute -top-12 -right-12 h-32 w-32 rounded-full bg-primary/10 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-2">
              <span className="h-8 w-8 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center flex-shrink-0">
                <Target className="h-4 w-4 text-primary" />
              </span>
              <span className="font-heading text-xs uppercase tracking-[0.2em] text-primary">Goal Countdown</span>
            </div>
            <p className="font-heading text-4xl bg-gradient-to-b from-white to-primary bg-clip-text text-transparent leading-tight">
              {daysUntilGoal} <span className="text-lg text-primary">Days</span>
            </p>
            <p className="text-muted-foreground text-xs mt-1">Until: {format(goalDate, 'MMMM d, yyyy')}</p>
            {formData.fitness_goal && (
              <p className="text-white/80 text-sm mt-3 pt-3 border-t border-primary/20">
                <span className="text-muted-foreground">Goal:</span> {formData.fitness_goal}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Trophy Case */}
      <button
        onClick={() => navigate('/badges')}
        className="group w-full rounded-2xl bg-white/[0.02] border border-white/10 hover:border-primary/30 hover:bg-white/[0.04] p-4 mt-6 text-left transition-all"
        data-testid="trophy-case-link"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="h-7 w-7 rounded-full bg-primary/10 border border-primary/25 flex items-center justify-center">
              <Trophy className="h-3.5 w-3.5 text-primary" />
            </span>
            <span className="font-heading text-xs uppercase tracking-[0.2em] text-primary">Trophy Case</span>
          </div>
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground group-hover:text-primary transition-colors">
            {earnedBadges.length}<span className="text-white/30">/</span>{totalBadges}
            <ChevronRight className="h-3 w-3" />
          </span>
        </div>
        {earnedBadges.length > 0 ? (
          <div className="flex gap-2 flex-wrap">
            {earnedBadges.slice(0, 6).map((code) => (
              <UserBadge key={code} code={code} size="lg" />
            ))}
          </div>
        ) : (
          <div className="text-muted-foreground text-xs italic">
            No badges yet — your first milestone unlocks at 25 workouts.
          </div>
        )}
      </button>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-2 mt-6">
        <div className="rounded-2xl bg-white/[0.02] border border-white/10 hover:border-primary/25 hover:bg-white/[0.04] p-4 text-center transition-all">
          <Cake className="h-4 w-4 text-primary mx-auto mb-2" />
          <p className="font-heading text-xl text-white leading-none">{formData.age || '—'}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1.5">Age</p>
        </div>
        <div className="rounded-2xl bg-white/[0.02] border border-white/10 hover:border-primary/25 hover:bg-white/[0.04] p-4 text-center transition-all">
          <Scale className="h-4 w-4 text-primary mx-auto mb-2" />
          <p className="font-heading text-xl text-white leading-none">{formData.weight || '—'}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1.5">{unitLabel(weightUnit)}</p>
        </div>
        <div className="rounded-2xl bg-white/[0.02] border border-white/10 hover:border-primary/25 hover:bg-white/[0.04] p-4 text-center transition-all">
          <Ruler className="h-4 w-4 text-primary mx-auto mb-2" />
          <p className="font-heading text-xl text-white leading-none">{formData.height || '—'}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1.5">in</p>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-heading text-lg text-white uppercase tracking-wider flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            Notifications
          </h4>
          {notificationsEnabled && (
            <Button
              onClick={onToggleNotificationSettings}
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-white"
            >
              <Settings className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        {!notificationsEnabled ? (
          <Button
            onClick={onEnableNotifications}
            disabled={loadingNotifications}
            className="w-full bg-primary/20 border border-primary/50 text-primary hover:bg-primary/30"
            data-testid="enable-notifications-btn"
          >
            {loadingNotifications ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Bell className="h-4 w-4 mr-2" />
            )}
            Enable Workout Reminders
          </Button>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/30">
              <span className="text-green-400 text-sm flex items-center gap-2">
                <Check className="h-4 w-4" />
                Notifications enabled
              </span>
              <Button
                onClick={onDisableNotifications}
                variant="ghost"
                size="sm"
                disabled={loadingNotifications}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 text-xs"
              >
                {loadingNotifications ? <Loader2 className="h-3 w-3 animate-spin" /> : <BellOff className="h-3 w-3 mr-1" />}
                Disable
              </Button>
            </div>
            
            <AnimatePresence>
              {showNotificationSettings && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="space-y-4 pt-3">
                    <div className="space-y-2">
                      <Label className="text-white text-sm">Reminder Time</Label>
                      <Input
                        type="time"
                        value={notificationSettings.workout_time}
                        onChange={(e) => onNotificationSettingsChange({ ...notificationSettings, workout_time: e.target.value })}
                        className="bg-black border-border text-white"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-white text-sm">Workout Days</Label>
                      <div className="flex flex-wrap gap-1">
                        {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                          <button
                            key={day}
                            type="button"
                            onClick={() => onToggleDay(day)}
                            className={`px-2 py-1 text-xs uppercase tracking-wider rounded ${
                              notificationSettings.days.includes(day)
                                ? 'bg-primary text-black'
                                : 'bg-white/10 text-white'
                            }`}
                          >
                            {day.slice(0, 3)}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationSettings.message_notifications}
                          onChange={(e) => onNotificationSettingsChange({ ...notificationSettings, message_notifications: e.target.checked })}
                          className="accent-primary"
                        />
                        <span className="text-white text-sm">New message alerts</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationSettings.progress_reminders}
                          onChange={(e) => onNotificationSettingsChange({ ...notificationSettings, progress_reminders: e.target.checked })}
                          className="accent-primary"
                        />
                        <span className="text-white text-sm">Weekly progress reminders</span>
                      </label>
                    </div>
                    
                    <Button
                      onClick={onSaveNotificationSettings}
                      size="sm"
                      className="w-full bg-primary text-black hover:bg-primary/90"
                    >
                      Save Settings
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
      </div>
    </motion.div>

    {/* Image Lightbox */}
    <AnimatePresence>
      {enlargedImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4"
          onClick={() => setEnlargedImage(null)}
          data-testid="profile-image-lightbox"
        >
          <button
            onClick={() => setEnlargedImage(null)}
            className="absolute top-6 right-6 text-white hover:text-primary transition-colors"
          >
            <X className="h-8 w-8" />
          </button>
          <motion.img
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            src={enlargedImage}
            alt="Profile"
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
};
